import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Paper,
  Container,
} from "@mui/material";
import { Print as PrintIcon, Download as DownloadIcon, ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { config } from "../../../config/config";
import { invokeGetApi } from "../../../services/apiServices";
import { InvoiceDocument, printInvoiceElement, PURPLE } from "../../common/InvoiceDocument";

const InvoiceView = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const printRef = useRef();

  useEffect(() => {
    if (!token) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    invokeGetApi(`/invoice/view/${token}`, {})
      .then((res) => {
        if (res?.status === 200) {
          setInvoice(res.data?.invoice || res.data?.data?.invoice || null);
        } else {
          setNotFound(true);
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [token]);

  const handleDownload = () => {
    if (printRef.current && invoice) {
      printInvoiceElement(printRef.current, `Invoice-${invoice.invoice_number || "TaxInvoice"}`);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: "70vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <CircularProgress sx={{ color: PURPLE }} />
      </Box>
    );
  }

  if (notFound || !invoice) {
    return (
      <Container maxWidth="sm" sx={{ py: 10 }}>
        <Paper sx={{ p: 4, textAlign: "center", borderRadius: 3, border: "1px solid #E2E8F0" }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: "#1A237E", mb: 1 }}>
            Invoice Not Found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            The requested invoice token is invalid, expired, or has been removed.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate("/")}
            sx={{ backgroundColor: PURPLE, borderRadius: 2, textTransform: "none", fontWeight: 700 }}
          >
            Return to Home
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Box sx={{ backgroundColor: "#F4F5F9", minHeight: "100vh", py: { xs: 2, md: 4 } }}>
      <Container maxWidth="md">
        {/* Top bar with Print Actions */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2.5,
            flexWrap: "wrap",
            gap: 1.5,
          }}
        >
          <Button
            variant="outlined"
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/")}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5 }}
          >
            Back to Home
          </Button>

          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handleDownload}
            sx={{
              backgroundColor: PURPLE,
              "&:hover": { backgroundColor: "#6454DB" },
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 1.5,
              px: 3,
            }}
          >
            Download / Print PDF
          </Button>
        </Box>

        {/* Invoice Container */}
        <InvoiceDocument data={invoice} invoiceRef={printRef} />
      </Container>
    </Box>
  );
};

export default InvoiceView;
